import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Trophy } from 'lucide-react';
import axios from 'axios';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/games`)
      .then((res) => setGames(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          data-testid="back-home"
          onClick={() => navigate('/jungle-math')}
          className="bg-white/70 p-2.5 rounded-full hover:bg-white/90 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-amber-800" />
        </button>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-amber-950 flex items-center gap-2">
          <Trophy className="w-8 h-8 text-yellow-500" />
          My Scores
        </h1>
      </div>

      {loading ? (
        <div className="glass-panel p-8 text-center">
          <p className="font-body text-lg text-amber-800">Loading scores...</p>
        </div>
      ) : games.length === 0 ? (
        <div className="glass-panel p-8 text-center">
          <p className="font-heading text-2xl font-bold text-amber-950 mb-2">No games yet!</p>
          <p className="font-body text-base text-amber-800 mb-4">Play a game to see your scores here.</p>
          <button
            data-testid="start-playing"
            onClick={() => navigate('/jungle-math')}
            className="bg-orange-500 text-white font-black text-base py-3 px-6 rounded-full border-b-[6px] border-orange-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-orange-400"
          >
            Start Playing
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {games.map((game, idx) => (
            <div
              key={game.id || idx}
              data-testid={`game-result-${idx}`}
              className="glass-panel p-4 flex items-center gap-4"
            >
              {/* Stars */}
              <div className="flex flex-col items-center min-w-[50px]">
                <div className="flex gap-0.5">
                  {[1, 2, 3].map((s) => (
                    <img
                      key={s}
                      src={STAR_URL}
                      alt="star"
                      className={`w-5 h-5 ${s <= game.stars ? '' : 'grayscale opacity-30'}`}
                    />
                  ))}
                </div>
              </div>

              {/* Score */}
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-black text-amber-950">
                    {game.score}/{game.total_questions}
                  </span>
                  <span className="font-body text-sm font-bold text-amber-700">
                    ({game.percentage}%)
                  </span>
                </div>
                <div className="flex gap-2 mt-1">
                  <span className="bg-amber-100 text-amber-800 font-bold text-xs px-2 py-0.5 rounded-full">
                    {game.mode === 'test' ? 'Test' : 'Practice'}
                  </span>
                  <span className="bg-amber-100 text-amber-800 font-bold text-xs px-2 py-0.5 rounded-full capitalize">
                    {game.operation}
                  </span>
                </div>
              </div>

              {/* Date */}
              <span className="font-body text-xs text-amber-700">{formatDate(game.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
